'use client';

import { c as _c } from "react/compiler-runtime";
import type { Data } from '@repo/strapi-types';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import SearchInput from '@/app/[locale]/blog/_components/SearchInput';
import AppLink from '@/components/elementary/AppLink';
import { GridEntries } from '@/components/shared/components/GridEntries';
import { MobileCategoryNavigation } from '@/components/shared/components/MobileCategoryNavigation';
import { SidePanelCategoryNavigation } from '@/components/shared/components/SidePanelCategoryNavigation';
import Heading from '@/components/typography/Heading';
import { Paragraph } from '@/components/typography/Paragraph';
import type { GridCardProps, GridCategoryNavigationData, GridCategoryNavigationItem, GridTypeProps } from '@/types/general';
import type { ServerRenderedEntry } from '@/types/helpers';
function findCategoryBySlug(allCategories: GridCategoryNavigationItem[], slug?: string | null): GridCategoryNavigationItem | undefined {
  if (!slug) return undefined;
  return allCategories.find(category => category?.slug === slug);
}
function filterEntriesByCategory(entries: ServerRenderedEntry<GridCardProps>[] | null | undefined, categoryDocumentId: string | undefined): ServerRenderedEntry<GridCardProps>[] {
  if (!entries) return [];
  if (!categoryDocumentId) return entries;
  return entries.filter(entry => entry?.categories?.some(category => category?.documentId === categoryDocumentId));
}
function filterEntriesBySearchString(entries: ServerRenderedEntry<GridCardProps>[], searchString: string): ServerRenderedEntry<GridCardProps>[] {
  if (!searchString) return entries;
  const lowerCaseSearchString = searchString.toLowerCase();
  return entries.filter(entry => entry.title?.toLowerCase().includes(lowerCaseSearchString));
}
function hasCategories(categories?: GridCategoryNavigationData): boolean {
  return Array.isArray(categories?.allCategories) && categories.allCategories.length > 0;
}
function isSearchableType(type: GridTypeProps): boolean {
  return type === 'partners' || type === 'apps';
}
export const GridWithCategoryFilters = t0 => {
  const $ = _c(64);
  const {
    entries,
    categories,
    type,
    additionalLink,
    categorySlug
  } = t0;
  const t = useTranslations("GridWithCategoryFilters");
  const {
    allCategories
  } = categories;
  let currentCategory;
  let t1;
  if ($[0] !== allCategories || $[1] !== categorySlug || $[2] !== entries) {
    currentCategory = findCategoryBySlug(allCategories, categorySlug);
    t1 = !categorySlug ? entries ?? [] : filterEntriesByCategory(entries, currentCategory?.documentId);
    $[0] = allCategories;
    $[1] = categorySlug;
    $[2] = entries;
    $[3] = currentCategory;
    $[4] = t1;
  } else {
    currentCategory = $[3];
    t1 = $[4];
  }
  const allEntries = t1;
  const [filteredEntries, setFilteredEntries] = useState(allEntries);
  let t2;
  if ($[5] !== allEntries) {
    t2 = searchString => {
      setFilteredEntries(filterEntriesBySearchString(allEntries, searchString));
    };
    $[5] = allEntries;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const searchItems = t2;
  let t3;
  if ($[7] !== filteredEntries) {
    t3 = filteredEntries.filter(_temp);
    $[7] = filteredEntries;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const featuredEntries = t3;
  let t4;
  if ($[9] !== filteredEntries) {
    t4 = filteredEntries.filter(_temp2);
    $[9] = filteredEntries;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const restEntries = t4;
  let t5;
  if ($[11] !== categories || $[12] !== categorySlug || $[13] !== t || $[14] !== type) {
    t5 = hasCategories(categories) && <MobileCategoryNavigation {...categories} triggerLabel={t(`allLabel.${type}`)} categorySlug={categorySlug} />;
    $[11] = categories;
    $[12] = categorySlug;
    $[13] = t;
    $[14] = type;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== additionalLink || $[17] !== categories || $[18] !== categorySlug) {
    t6 = <SidePanelCategoryNavigation {...categories} additionalLink={additionalLink} categorySlug={categorySlug} />;
    $[16] = additionalLink;
    $[17] = categories;
    $[18] = categorySlug;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== searchItems || $[21] !== type) {
    t7 = isSearchableType(type) && <SearchInput className="mb-6" onInputChange={searchItems} customOnSearch={searchItems} />;
    $[20] = searchItems;
    $[21] = type;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== filteredEntries.length || $[24] !== t) {
    t8 = filteredEntries.length === 0 && <Paragraph className="mb-6">{t("noResults")}</Paragraph>;
    $[23] = filteredEntries.length;
    $[24] = t;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== currentCategory?.title || $[27] !== type) {
    t9 = type === "partners" && <Heading tag="h2" fontSize="displaySmall">{currentCategory?.title}</Heading>;
    $[26] = currentCategory?.title;
    $[27] = type;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== featuredEntries.length || $[30] !== t || $[31] !== type) {
    t10 = type === "apps" && featuredEntries.length > 0 ? <Heading tag="h2" fontSize="displaySmall">{t("featuredApps")}</Heading> : null;
    $[29] = featuredEntries.length;
    $[30] = t;
    $[31] = type;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== featuredEntries || $[34] !== type) {
    t11 = featuredEntries.length > 0 && <GridEntries entries={featuredEntries} type={type} />;
    $[33] = featuredEntries;
    $[34] = type;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== restEntries.length || $[37] !== t || $[38] !== type) {
    t12 = type === "apps" && restEntries.length > 0 ? <Heading tag="h2" fontSize="displaySmall">{t("allApps")}</Heading> : null;
    $[36] = restEntries.length;
    $[37] = t;
    $[38] = type;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== currentCategory?.title || $[41] !== type) {
    t13 = type === "library" && <Heading tag="h2" fontSize="displaySmall" className="sr-only">{currentCategory?.title}</Heading>;
    $[40] = currentCategory?.title;
    $[41] = type;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== restEntries || $[44] !== type) {
    t14 = restEntries.length > 0 && <GridEntries entries={restEntries} type={type} />;
    $[43] = restEntries;
    $[44] = type;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== t10 || $[47] !== t11 || $[48] !== t12 || $[49] !== t13 || $[50] !== t14 || $[51] !== t9) {
    t15 = <div className="flex flex-col gap-7.5">{t9}{t10}{t11}{t12}{t13}{t14}</div>;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
    $[51] = t9;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t15 || $[54] !== t7 || $[55] !== t8) {
    t16 = <div className="flex flex-1 flex-col">{t7}{t8}{t15}</div>;
    $[53] = t15;
    $[54] = t7;
    $[55] = t8;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== additionalLink) {
    t17 = additionalLink?.href && <div className="border-t border-neutral-light-40 pt-9 pb-9 lg:hidden"><AppLink link={additionalLink} size="none" variant="ghost">{additionalLink.label}</AppLink></div>;
    $[57] = additionalLink;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] !== t16 || $[60] !== t17 || $[61] !== t5 || $[62] !== t6) {
    t18 = <div className="flex lg:flex-row flex-col gap-8 justify-between">{t5}{t6}{t16}{t17}</div>;
    $[59] = t16;
    $[60] = t17;
    $[61] = t5;
    $[62] = t6;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  return t18;
};
function _temp(entry) {
  return entry?.isFeatured;
}
function _temp2(entry_0) {
  return !entry_0?.isFeatured;
}